// @ts-strict-ignore
import type { SerializedStyles } from "@emotion/serialize";
import { useTranslation } from "react-i18next";

import { Accordion, Checkbox } from "@aviary";
import { FilterType } from "@shared/hooks/useAnalyticsLogger/useAnalyticsLogger";
import { useCatalogSearch } from "@shared/hooks/useCatalogSearch/useCatalogSearch";
import { l } from "@shared/locales/i18n";
import type { EqpLevelType } from "@shared/types/graphqlGenerated";

import { FilterTitle } from "../shared/FilterTitle/FilterTitle";

import * as styles from "./QualityPartners.styles";

interface Props {
  isBold?: boolean;
  logFilterEvent: (entityName: string, entityType: string) => void;
  customAccordionStyle?: SerializedStyles[] | SerializedStyles;
}

export const QUALITY_PARTNERS: EqpLevelType[] = ["Gold", "Silver"];

const QualityPartners = ({ isBold, logFilterEvent, customAccordionStyle }: Props) => {
  const { t } = useTranslation();
  const { activeEqpLevels, setActiveEqpLevels } = useCatalogSearch();

  const activeQualityPartnerCount = activeEqpLevels?.length;

  const isQualityPartnerActive = (eqpLevel: string): boolean =>
    activeEqpLevels.some(eqp => eqp === eqpLevel);

  const handleQualityPartnerSelect = (eqpLevel: EqpLevelType) => {
    const distanceFromTop = window?.scrollY;
    if (isQualityPartnerActive(eqpLevel)) {
      setActiveEqpLevels(activeEqpLevels.filter(eqp => eqp !== eqpLevel));
    } else {
      logFilterEvent(eqpLevel, FilterType.EQP_LEVELS);
      setActiveEqpLevels([...activeEqpLevels, eqpLevel]);
    }
    requestAnimationFrame?.(() => window?.scrollTo(0, distanceFromTop));
  };

  const renderQualityPartners = () =>
    QUALITY_PARTNERS.map((eqpLevel, i) => {
      return (
        <span key={i} css={styles.checkbox}>
          <Checkbox
            isFullWidth
            checked={isQualityPartnerActive(eqpLevel)}
            onChange={() => handleQualityPartnerSelect(eqpLevel)}
          >
            {eqpLevel}
          </Checkbox>
        </span>
      );
    });

  return (
    <Accordion
      css={customAccordionStyle}
      isDefaultExpanded={activeQualityPartnerCount > 0}
      coreTriggerContent={
        <FilterTitle
          title={t(l.sharedCatalog.filter.QualityPartner)}
          count={activeQualityPartnerCount}
          isBadge
          isSecondaryWeight={isBold}
        />
      }
    >
      <div css={styles.checkboxList}>{renderQualityPartners()}</div>
    </Accordion>
  );
};

export { QualityPartners };
